import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MapContainer, TileLayer, Polygon, Polyline, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, catLabel } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MagnifyingGlass, FloppyDisk, ArrowUUpLeft, Trash, MapPin, ArrowsHorizontal, Polygon as PolygonIcon } from "@phosphor-icons/react";
import { alignPointsToOutline } from "@/lib/alignSlots";

const CAT_COLORS = { ΜΙΚΡΑ: "#0055FF", ΜΕΣΑΙΑ: "#234E7D", ΜΕΓΑΛΑ: "#7C3AED" };
const colorFor = (name) => CAT_COLORS[name] || "#0F766E";

function numberedIcon(num, bg, label) {
  const main = label || num;
  const sub = label ? `<div style="font-size:9px;opacity:.85;line-height:1">${num}</div>` : "";
  const w = Math.max(30, String(main).length * 8 + 14);
  return L.divIcon({
    className: "",
    html: `<div style="min-width:${w}px;height:30px;padding:0 7px;border-radius:15px;background:${bg};color:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;font-size:12px;font-weight:700;border:2px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.45);white-space:nowrap"><div style="line-height:1">${main}</div>${sub}</div>`,
    iconSize: [w, 30],
    iconAnchor: [w / 2, 15],
  });
}

function ClickCapture({ onClick }) {
  useMapEvents({ click: (e) => onClick([e.latlng.lat, e.latlng.lng]) });
  return null;
}

export default function PlotMap() {
  const qc = useQueryClient();
  const mapRef = useRef(null);

  const { data: config } = useQuery({ queryKey: ["config"], queryFn: async () => (await api.get("/config")).data });
  const { data: slots = [] } = useQuery({ queryKey: ["slots"], queryFn: async () => (await api.get("/slots")).data });

  const [outline, setOutline] = useState([]);
  const [points, setPoints] = useState({});
  const [hydrated, setHydrated] = useState(false);
  const [mode, setMode] = useState("slots");
  const [selectedCat, setSelectedCat] = useState("");
  const [lineStart, setLineStart] = useState(null);
  const [lineCount, setLineCount] = useState("");
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);

  // Hydrate saved layout once config actually arrives (avoids empty-init race).
  useEffect(() => {
    if (config && !hydrated) {
      const l = config.layout || {};
      setOutline(Array.isArray(l.outline) ? l.outline.map((p) => [p[0], p[1]]) : []);
      setPoints(l.points && typeof l.points === "object" ? { ...l.points } : {});
      setHydrated(true);
    }
  }, [config, hydrated]);

  const categories = config?.categories || [];
  const byNumber = {};
  slots.forEach((s) => { byNumber[s.number] = s; });

  const slotsOfCat = (catId) => slots.filter((s) => s.category_id === catId).sort((a, b) => Number(a.number) - Number(b.number));
  const placedCount = (catId) => slotsOfCat(catId).filter((s) => points[s.number] != null).length;

  const handleMapClick = (latlng) => {
    if (mode === "outline") {
      setOutline((o) => [...o, latlng]);
    } else if (mode === "slots" && selectedCat) {
      const next = slotsOfCat(selectedCat).find((s) => points[s.number] == null);
      if (!next) return toast.error("Έχουν τοποθετηθεί όλες οι θέσεις αυτής της κατηγορίας");
      setPoints((p) => ({ ...p, [next.number]: latlng }));
    } else if (mode === "slots" && !selectedCat) {
      toast.error("Επιλέξτε πρώτα κατηγορία θέσεων");
    } else if (mode === "line") {
      if (!selectedCat) return toast.error("Επιλέξτε πρώτα κατηγορία θέσεων");
      const cnt = Number(lineCount);
      if (!cnt || cnt < 1) return toast.error("Ορίστε πλήθος σκαφών στη σειρά");
      if (!lineStart) {
        setLineStart(latlng);
        toast.message("Πατήστε το σημείο τέλους της σειράς");
        return;
      }
      const avail = slotsOfCat(selectedCat).filter((s) => points[s.number] == null);
      if (!avail.length) { setLineStart(null); return toast.error("Δεν υπάρχουν διαθέσιμες θέσεις σε αυτή την κατηγορία"); }
      const n = Math.min(cnt, avail.length);
      const np = { ...points };
      for (let i = 0; i < n; i++) {
        const t = n > 1 ? i / (n - 1) : 0.5;
        np[avail[i].number] = [lineStart[0] + t * (latlng[0] - lineStart[0]), lineStart[1] + t * (latlng[1] - lineStart[1])];
      }
      setPoints(np);
      setLineStart(null);
      if (n < cnt) toast.warning(`Τοποθετήθηκαν ${n} (δεν υπήρχαν άλλες διαθέσιμες θέσεις)`);
      else toast.success(`Τοποθετήθηκαν ${n} θέσεις σε ευθεία σειρά`);
    }
  };

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const res = (await api.get("/geocode", { params: { q: query.trim() } })).data;
      if (res && res.lat != null && mapRef.current) {
        mapRef.current.setView([res.lat, res.lon], 18);
      } else {
        toast.error("Δεν βρέθηκε η τοποθεσία");
      }
    } catch {
      toast.error("Δεν βρέθηκε η τοποθεσία");
    } finally {
      setSearching(false);
    }
  };

  const save = async () => {
    const map = mapRef.current;
    const center = map ? [map.getCenter().lat, map.getCenter().lng] : null;
    const zoom = map ? map.getZoom() : null;
    try {
      const aligned = alignPointsToOutline(points, outline) || points;
      await api.put("/config/layout", { outline, center, zoom, points: aligned });
      setPoints(aligned);
      toast.success("Το σχέδιο του οικοπέδου αποθηκεύτηκε");
      qc.invalidateQueries({ queryKey: ["config"] });
    } catch (e) {
      const d = e.response?.data?.detail;
      console.error("layout save error", e.response?.status, d, e);
      toast.error(typeof d === "string" ? d : `Σφάλμα αποθήκευσης (${e.response?.status || "δίκτυο"})`);
    }
  };

  const startCenter = config?.layout?.center || [37.98, 23.72];
  const startZoom = config?.layout?.zoom || (config?.layout?.center ? 18 : 6);

  return (
    <div>
      <PageHeader
        title="Σχεδίαση κάτοψης θέσεων"
        subtitle="Εντοπίστε το οικόπεδο σε δορυφορική εικόνα, χαράξτε το περίγραμμα και τοποθετήστε τις θέσεις"
        action={
          <Button onClick={save} className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" data-testid="plot-save-btn">
            <FloppyDisk size={18} weight="bold" /> Αποθήκευση
          </Button>
        }
      />
      <div className="grid gap-4 p-6 lg:grid-cols-[320px_1fr]">
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Αναζήτηση τοποθεσίας…" className="rounded-sm" data-testid="plot-search-input" />
            <Button variant="outline" className="rounded-sm" onClick={search} disabled={searching} data-testid="plot-search-btn"><MagnifyingGlass size={16} /></Button>
          </div>

          <div className="rounded-sm border border-border p-3">
            <div className="mb-2 text-xs font-semibold text-muted-foreground">Λειτουργία</div>
            <div className="flex gap-2">
              <Button size="sm" variant={mode === "slots" ? "default" : "outline"} className={`flex-1 rounded-sm ${mode === "slots" ? "bg-[#0055FF]" : ""}`} onClick={() => { setMode("slots"); setLineStart(null); }} data-testid="plot-mode-slots"><MapPin size={14} weight="bold" /> Θέσεις</Button>
              <Button size="sm" variant={mode === "line" ? "default" : "outline"} className={`flex-1 rounded-sm ${mode === "line" ? "bg-[#0F766E]" : ""}`} onClick={() => { setMode("line"); setLineStart(null); }} data-testid="plot-mode-line"><ArrowsHorizontal size={14} weight="bold" /> Σειρά</Button>
              <Button size="sm" variant={mode === "outline" ? "default" : "outline"} className={`flex-1 rounded-sm ${mode === "outline" ? "bg-[#234E7D]" : ""}`} onClick={() => { setMode("outline"); setLineStart(null); }} data-testid="plot-mode-outline"><PolygonIcon size={14} weight="bold" /> Περίγραμμα</Button>
            </div>
          </div>

          {mode === "outline" ? (
            <div className="rounded-sm border border-border p-3 text-sm">
              <p className="text-xs text-muted-foreground">Πατήστε στον χάρτη για να προσθέσετε κορυφές του περιγράμματος του οικοπέδου.</p>
              <div className="mt-2 text-xs">Κορυφές: <b className="tabular-nums">{outline.length}</b></div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setOutline((o) => o.slice(0, -1))} data-testid="plot-undo-point"><ArrowUUpLeft size={14} /> Αναίρεση</Button>
                <Button size="sm" variant="outline" className="rounded-sm text-destructive" onClick={() => setOutline([])} data-testid="plot-clear-outline"><Trash size={14} /> Καθαρισμός</Button>
              </div>
            </div>
          ) : (
            <div className="rounded-sm border border-border p-3">
              <div className="mb-2 text-xs font-semibold text-muted-foreground">{mode === "line" ? "Επιλέξτε κατηγορία, δηλώστε πλήθος & τραβήξτε τη σειρά" : "Επιλέξτε κατηγορία & πατήστε στον χάρτη"}</div>
              {mode === "line" && (
                <div className="mb-3">
                  <label className="text-xs text-muted-foreground">Πλήθος σκαφών σε αυτή τη σειρά</label>
                  <Input type="number" min="1" value={lineCount} onChange={(e) => setLineCount(e.target.value)} className="mt-1 rounded-sm" data-testid="plot-line-count" />
                  <p className="mt-1.5 text-[11px] text-muted-foreground">{lineStart ? "Πατήστε το σημείο τέλους της σειράς." : "Πατήστε το σημείο έναρξης, μετά το τέλος της σειράς."}</p>
                </div>
              )}
              <div className="space-y-1.5">
                {categories.map((c) => {
                  const total = slotsOfCat(c.id).length;
                  const placed = placedCount(c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => setSelectedCat(c.id)}
                      data-testid={`plot-cat-${c.id}`}
                      className={`flex w-full items-center justify-between rounded-sm border px-3 py-2 text-sm transition-colors ${selectedCat === c.id ? "border-[#0055FF] bg-[#EFF6FF]" : "border-border hover:bg-secondary/50"}`}
                    >
                      <span className="flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full" style={{ background: colorFor(c.name) }} />
                        {catLabel(c.name)}
                      </span>
                      <span className="tabular-nums text-xs text-muted-foreground">{placed}/{total}</span>
                    </button>
                  );
                })}
              </div>
              <p className="mt-3 text-[11px] text-muted-foreground">Σύρετε μια θέση για μετακίνηση. Διπλό κλικ σε θέση για αφαίρεση.</p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" className="w-full rounded-sm text-destructive" onClick={() => { setPoints({}); setLineStart(null); toast.message("Οι θέσεις αφαιρέθηκαν από τον χάρτη"); }} data-testid="plot-clear-slots"><Trash size={14} /> Αναίρεση θέσεων</Button>
              </div>
            </div>
          )}
        </div>

        <div className="overflow-hidden rounded-sm border border-border" data-testid="plot-map">
          <MapContainer ref={mapRef} center={startCenter} zoom={startZoom} maxZoom={22} className={mode === "outline" || ((mode === "slots" || mode === "line") && selectedCat) ? "marina-crosshair" : ""} style={{ height: "72vh", width: "100%" }} scrollWheelZoom>
            <TileLayer
              url="https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
              subdomains={["mt0", "mt1", "mt2", "mt3"]}
              attribution="Δορυφορική εικόνα &copy; Google"
              maxZoom={22}
              maxNativeZoom={21}
            />
            <ClickCapture onClick={handleMapClick} />
            {lineStart && (
              <Marker position={lineStart} icon={numberedIcon("A", "#0F766E")} />
            )}
            {outline.length >= 3 ? (
              <Polygon positions={outline} pathOptions={{ color: "#10B981", weight: 3, fillColor: "#10B981", fillOpacity: 0.28 }} />
            ) : outline.length === 2 ? (
              <Polyline positions={outline} pathOptions={{ color: "#10B981", weight: 3 }} />
            ) : null}
            {Object.entries(points).map(([num, ll]) => {
              const s = byNumber[num];
              return (
                <Marker
                  key={num}
                  position={ll}
                  draggable
                  icon={numberedIcon(num, colorFor(s?.category_name), s?.label)}
                  eventHandlers={{
                    dragend: (e) => { const { lat, lng } = e.target.getLatLng(); setPoints((p) => ({ ...p, [num]: [lat, lng] })); },
                    dblclick: () => setPoints((p) => { const n = { ...p }; delete n[num]; return n; }),
                  }}
                />
              );
            })}
          </MapContainer>
        </div>
      </div>
    </div>
  );
}
